import {
  NOTKA_NIEZWERYFIKOWANEGO_CYTATU,
  pierwszyWariant,
  type ZnaleziskoUzytkownika,
} from "../../lib/wynikUzytkownika.js";
import { IconKopiuj } from "../icons/index.js";
import { Badge } from "../ui/Badge.js";
import { useToast } from "../ui/Toast.js";

/**
 * Jedno wskazanie z oceny w widoku uzytkownika: plakietka rodzaju (mocna strona albo do poprawy), plakietka parametru,
 * numer wypowiedzi, cytat, uzasadnienie, a przy rzeczy do poprawy blok "Lepiej powiedzieć:" z pierwszym wariantem i
 * przyciskiem "Kopiuj".
 *
 * Kolory oceny wg decyzji autora metodyki (29.10.2026): mocna strona jest zlota, rzecz do poprawy szara (plakietka,
 * kreska przy cytacie, kropka przy lepszym wariancie: uzytkownik.css na tokenach --ocena-*). Znaczenie niesie zawsze
 * slowo na plakietce (ST-3).
 *
 * CYTAT POKAZUJEMY TYLKO ZWERYFIKOWANY: kod sprawdzil, ze ten fragment faktycznie stoi w wypowiedzi (evidenceVerified /
 * quoteVerified). Przy niezweryfikowanym zostaje ta sama notka co w karcie administratora, a sam fragment nie jest
 * cytowany: twierdzenie, ze ktos cos powiedzial, musi miec pokrycie w transkrypcie. Nie pokazujemy tez wskazan
 * technicznych (nazwy modelu, liczby pewnosci).
 *
 * Numer wypowiedzi to przycisk: rozwija transkrypt i przewija do tej wypowiedzi (onPokaz).
 */
export function Znalezisko({
  element,
  onPokaz,
}: {
  element: ZnaleziskoUzytkownika;
  /** Pokazuje wypowiedz o tym indeksie (od 0) w transkrypcie. */
  onPokaz: (indeks: number) => void;
}): React.JSX.Element {
  const toast = useToast();
  const { wskazanie: w, parametr } = element;
  const mocna = w.rodzaj === "mocna-strona";
  const lepiej = mocna ? null : pierwszyWariant(w);

  async function kopiuj(tekst: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(tekst);
      toast.sukces("Skopiowano do schowka.");
    } catch {
      // Brak schowka (stara przegladarka, adres bez https) albo odmowa uprawnien: nic nie wywala ekranu. To prawdziwy blad
      // operacji, wiec toast bledu: czerwien nie koliduje ze zlotem i szaroscia oceny, a czytnik oglasza go od razu
      // (role="alert"). Nie ostrzezenie: w widoku oceny bursztyn (--status-warning) znaczy "najlepsze".
      toast.blad("Nie udało się skopiować. Zaznacz tekst i skopiuj go ręcznie.");
    }
  }

  return (
    <article className="znalezisko" data-rodzaj={w.rodzaj}>
      <div className="znalezisko__meta">
        <Badge wariant={mocna ? "zloto" : "szarosc"} kropka>
          {mocna ? "Mocna strona" : "Do poprawy"}
        </Badge>
        <Badge>{parametr}</Badge>
        {w.indeks !== null && w.numer !== null && (
          <button
            type="button"
            className="znalezisko__skok"
            onClick={() => onPokaz(w.indeks as number)}
            aria-label={`Pokaż wypowiedź ${w.numer} w rozmowie`}
          >
            {`Wypowiedź ${w.numer}`}
          </button>
        )}
      </div>

      {w.cytat && w.cytatZweryfikowany && <blockquote className="znalezisko__cytat">{w.cytat}</blockquote>}
      {w.cytat && !w.cytatZweryfikowany && <p className="znalezisko__nota">{NOTKA_NIEZWERYFIKOWANEGO_CYTATU}</p>}

      {w.uzasadnienie && <p className="znalezisko__opis">{w.uzasadnienie}</p>}

      {lepiej && (
        <div className="znalezisko__lepiej">
          <span className="znalezisko__lepiej-etykieta">Lepiej powiedzieć:</span>
          <p className="znalezisko__lepiej-tekst">{lepiej}</p>
          <button
            type="button"
            className="znalezisko__kopiuj"
            onClick={() => void kopiuj(lepiej)}
            aria-label={`Kopiuj lepszą wersję${w.numer !== null ? ` wypowiedzi ${w.numer}` : ""}`}
          >
            <IconKopiuj size={14} />
            Kopiuj
          </button>
        </div>
      )}
    </article>
  );
}
